import { Buildings, Cube } from "@phosphor-icons/react";
import { PickerContentProps } from "../types";
import { REMOVAL_LABELS } from "../constants/pickerConstants";
import { useBuildingsAndUnits } from "hooks/useBuildingsAndUnits";

// Item components
import { HelpdeskLabelItem } from "./items/HelpdeskLabelItem";
import { HelpdeskStatusItem } from "./items/HelpdeskStatusItem";
import { FileLabelItem } from "./items/FileLabelItem";
import { SecondaryLabelItem } from "./items/SecondaryLabelItem";
import { UserItem, NotAssignedItem } from "./items/UserItem";
import { BuildingItem } from "./items/BuildingItem";
import { UnitItem } from "./items/UnitItem";
import { SupplierItem, FrequentSupplierItem } from "./items/SupplierItem";
import { ContactItem } from "./items/ContactItem";
import { SourceItem } from "./items/SourceItem";
import { StatusItem } from "./items/StatusItem";
import { DateInput } from "./items/DateInput";
import { ApiDataItem } from "./items/ApiDataItem";
import { CustomTagsItem } from "./items/CustomTagsItem";
import { TextInput } from "./items/TextInput";
import { NumberInput } from "./items/NumberInput";
import { AssetItem } from "./items/AssetItem";
import { BugPriorityItem } from "./items/BugPriorityItem";
import { BugTypeItem } from "./items/BugTypeItem";
import { BugStatusItem } from "./items/BugStatusItem";
import { EpicItem } from "./items/EpicItem";
import { RenovationProjectItem } from "./items/RenovationProjectItem";
import { ProjectPartItem } from "./items/ProjectPartItem";
import { CreateSupplierForm } from "./items/CreateSupplierForm";
import { ResourceItem } from "./items/ResourceItem";

export const PickerContent = ({
  mode,
  config,
  filteredData,
  selected,
  onSelect,
  isLoading,
  searchTerm,
  frequentSuppliers = [],
  onSupplierCreated,
  onSelectAll,
  onDeselectAll,
}: PickerContentProps) => {
  const { isLoading: buildingsLoading } = useBuildingsAndUnits();

  // Date modes render directly (no list)
  if (mode === 'document-date' || mode === 'expiration-date') {
    return <DateInput onSelect={onSelect} defaultValue={config.defaultValue as string} />;
  }

  // Text input mode (renders input field with confirm button managed by parent)
  if (mode === 'text-input') {
    return (
      <TextInput
        defaultValue={config.defaultValue as string}
        onValueChange={onSelect}
        placeholder={config.searchPlaceholder}
        suffix={config.suffix}
      />
    );
  }

  // Number input mode (renders number field with confirm button managed by parent)
  if (mode === 'number-input') {
    return (
      <NumberInput
        defaultValue={config.defaultValue as number}
        onValueChange={onSelect}
        placeholder={config.searchPlaceholder}
        min={config.apiConfig?.minSearchLength}
        max={config.apiConfig?.debounceMs}
        format={config.apiConfig?.format as 'currency' | 'number' | undefined}
        suffix={config.suffix}
      />
    );
  }

  const renderRemovalOption = () => {
    if (!config.allowRemoval) return null;

    // For unit mode, show the selected unit ID so the action is unambiguous
    // ("Verwijder 2800-KONI-ASTR-106-501" instead of generic "Geen unit").
    const buildLabel = (): string => {
      if (mode === 'unit' && selected) {
        const id = typeof selected === 'string' ? selected : selected?.id;
        if (id) return `Verwijder ${id}`;
      }
      return REMOVAL_LABELS[mode] || REMOVAL_LABELS.default;
    };

    return (
      <button
        onClick={() => onSelect(null)}
        className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors hover:bg-red-50 text-red-600"
      >
        {buildLabel()}
      </button>
    );
  };

  const renderNotAssignedOption = () => {
    if (mode !== 'assignee') return null;

    return (
      <NotAssignedItem
        isSelected={selected === null}
        onSelect={() => onSelect(null)}
      />
    );
  };

  const renderContextIndicator = () => {
    if (mode !== 'file-label' || (!config.currentBuildingId && !config.currentUnitId)) return null;

    return (
      <div className="mb-3 p-2 bg-muted rounded-md border border-muted-foreground/20">
        <div className="text-xs text-neutral-600 mb-1">
          Labels gefilterd op basis van:
        </div>
        <div className="flex items-center gap-2">
          {config.currentBuildingId && (
            <div className="flex items-center gap-1">
              <Buildings className="h-3 w-3 text-blue-600" />
              <span className="text-xs font-medium text-blue-800">Gebouw geselecteerd</span>
            </div>
          )}
          {config.currentUnitId && (
            <div className="flex items-center gap-1">
              <Cube className="h-3 w-3 text-green-600" />
              <span className="text-xs font-medium text-green-800">Unit geselecteerd</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSelectAllButtons = () => {
    // Only show for unit mode with multiSelect and data available
    if (mode !== 'unit' || !config.multiSelect || filteredData.length === 0) return null;
    if (!onSelectAll || !onDeselectAll) return null;

    const selectedCount = Array.isArray(selected) ? selected.length : 0;
    const allSelected = selectedCount === filteredData.length && filteredData.length > 0;

    return (
      <div className="mb-3 flex items-center justify-between p-2 bg-muted/50 rounded-md border border-muted-foreground/20">
        <div className="text-xs text-neutral-600">
          {selectedCount} van {filteredData.length} geselecteerd
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSelectAll}
            disabled={allSelected}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              allSelected
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20'
            }`}
          >
            Selecteer alles
          </button>
          <button
            onClick={onDeselectAll}
            disabled={selectedCount === 0}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              selectedCount === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-red-50 text-red-600 hover:bg-red-100'
            }`}
          >
            Deselecteer alles
          </button>
        </div>
      </div>
    );
  };

  const renderFrequentSuppliers = () => {
    if (mode !== 'supplier' || searchTerm.trim() || !config.showFrequentSuppliers || frequentSuppliers.length === 0) {
      return null;
    }

    return (
      <>
        <div className="px-3 py-2 text-xs font-semibold text-moss-primary border-b border-moss-primary/20">
          🔥 Veelgebruikte leveranciers voor label(s): {config.ticketLabels?.join(', ')}
        </div>
        {frequentSuppliers.map((freqSupplier) => (
          <FrequentSupplierItem
            key={`freq-${freqSupplier.supplier.id}`}
            item={freqSupplier}
            isSelected={selected?.id === freqSupplier.supplier.id}
            onSelect={onSelect}
          />
        ))}
        <div className="px-3 py-2 text-xs font-semibold text-neutral-secondary-mid border-b border-muted-foreground/20">
          Alle leveranciers
        </div>
      </>
    );
  };

  const renderLoadingState = () => {
    if ((mode === 'building' || mode === 'unit') && buildingsLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Laden...</div>;
    }

    if (mode === 'supplier' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Laden...</div>;
    }

    if (mode === 'reporter' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Zoeken...</div>;
    }

    if (mode === 'room' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Ruimtes laden...</div>;
    }

    if (mode === 'rental-contract' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Huurcontracten laden...</div>;
    }

    if (mode === 'asset' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondaryMid">Assets laden...</div>;
    }

    if (mode === 'renovation-project' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Renovatieprojecten zoeken...</div>;
    }

    if (mode === 'project-part' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Projectonderdelen laden...</div>;
    }

    if (mode === 'acquisition-file' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Acquisitiedossiers laden...</div>;
    }

    if (mode === 'resource' && isLoading) {
      return <div className="text-center py-4 text-neutral-secondary-mid">Contacten en bedrijven zoeken...</div>;
    }

    return null;
  };

  const renderEmptyState = () => {
    if (mode === 'reporter' && searchTerm.length < 2) {
      return (
        <div className="text-center py-4 text-neutral-secondary-mid">
          Type minimaal 2 karakters om te zoeken
        </div>
      );
    }

    if (mode === 'resource' && searchTerm.length < 2) {
      return (
        <div className="text-center py-4 text-neutral-secondary-mid">
          Type minimaal 2 karakters om contacten of bedrijven te zoeken
        </div>
      );
    }

    if (mode === 'renovation-project' && searchTerm.length < 2 && !config.currentBuildingId) {
      return (
        <div className="text-center py-4 text-neutral-secondary-mid">
          Type minimaal 2 karakters om renovatieprojecten te zoeken
        </div>
      );
    }

    if (mode === 'project-part' && !config.currentRenovationProjectId) {
      return (
        <div className="text-center py-4 text-neutral-secondary-mid">
          Selecteer eerst een renovatieproject
        </div>
      );
    }

    if (!buildingsLoading && !isLoading && filteredData.length === 0) {
      // For renovation-project: show message if building is selected OR search is provided
      if (mode === 'renovation-project' && (config.currentBuildingId || searchTerm.length >= 2)) {
        return (
          <div className="text-center py-4 text-neutral-secondary-mid">
            Geen resultaten gevonden
            {config.currentBuildingId && (
              <div className="text-xs mt-1">
                Geen renovatieprojecten gevonden voor dit gebouw
              </div>
            )}
          </div>
        );
      }

      // For project-part: show message if project is selected
      if (mode === 'project-part' && config.currentRenovationProjectId) {
        return (
          <div className="text-center py-4 text-neutral-secondary-mid">
            Geen resultaten gevonden
            <div className="text-xs mt-1">
              Geen projectonderdelen gevonden voor dit project
            </div>
          </div>
        );
      }

      // For other modes: show generic message if search term is long enough
      if (searchTerm.length >= 2) {
        return (
          <div className="text-center py-4 text-neutral-secondary-mid">
            Geen resultaten gevonden
          </div>
        );
      }
    }

    return null;
  };

  const renderDataItems = () => {
    return filteredData.map((item: any, index: number) => {
      const key = item.id || item.value || item || index;
      const isSelected = getIsSelected(item);

      switch (mode) {
        case 'helpdesk-label':
          return (
            <HelpdeskLabelItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'helpdesk-status':
          return (
            <HelpdeskStatusItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'file-label':
          return (
            <FileLabelItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'secondary-file-label':
          return (
            <SecondaryLabelItem
              key={key}
              item={item}
              onSelect={onSelect}
            />
          );

        case 'assignee':
          return (
            <UserItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'building':
          return (
            <BuildingItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'unit':
          return (
            <UnitItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
              showCheckbox={config.multiSelect}
            />
          );

        case 'supplier':
          return (
            <SupplierItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'reporter':
          return (
            <ContactItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'source':
          return (
            <SourceItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'status':
          return (
            <StatusItem
              key={key}
              item={item}
              onSelect={onSelect}
            />
          );

        case 'custom-tags':
          return (
            <CustomTagsItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'rental-contract':
          return (
            <ApiDataItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
              displayField={config.apiConfig?.displayField || 'name'}
              valueField={config.apiConfig?.valueField || 'id'}
              secondaryField={config.apiConfig?.secondaryField}
              iconField={config.apiConfig?.iconField}
            />
          );

        case 'asset':
          return (
            <AssetItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'bug-priority':
          return (
            <BugPriorityItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'bug-type':
          return (
            <BugTypeItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'bug-status':
          return (
            <BugStatusItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'epic':
          return (
            <EpicItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'renovation-project':
          return (
            <RenovationProjectItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'project-part':
          return (
            <ProjectPartItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
            />
          );

        case 'acquisition-file':
          return (
            <ApiDataItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
              displayField={config.apiConfig?.displayField || 'name'}
              valueField={config.apiConfig?.valueField || 'id'}
              secondaryField={config.apiConfig?.secondaryField || 'id'}
              iconField={config.apiConfig?.iconField}
            />
          );

        case 'resource':
          return (
            <ResourceItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
              showCheckbox={config.multiSelect}
            />
          );

        case 'room':
          return (
            <ApiDataItem
              key={key}
              item={item}
              isSelected={isSelected}
              onSelect={onSelect}
              displayField="roomType"
              valueField="id"
            />
          );

        default:
          return null;
      }
    });
  };

  const getIsSelected = (item: any) => {
    if (mode === 'status') {
      return selected === item.value;
    }
    if (mode === 'helpdesk-label' || mode === 'helpdesk-status' || mode === 'source' || mode === 'bug-priority' || mode === 'bug-type' || mode === 'bug-status') {
      return selected === item;
    }
    if (mode === 'custom-tags') {
      // For custom-tags, item can be string or {value, label} object
      const itemValue = typeof item === 'string' ? item : item.value;
      // selected could be a single value or array (multi-select)
      if (Array.isArray(selected)) {
        return selected.includes(itemValue);
      }
      return selected === itemValue;
    }
    if ((mode === 'unit' || mode === 'building') && config.multiSelect) {
      // For multi-select building/unit mode, check if item.id is in the selected array
      if (Array.isArray(selected)) {
        return selected.includes(item.id);
      }
      return false;
    }
    if (mode === 'epic') {
      // For epic, compare by id or by the whole epic object
      return selected?.id === item.id || selected === item.id;
    }
    if (mode === 'renovation-project' || mode === 'project-part') {
      // For renovation projects and parts, compare by id
      return selected?.id === item.id || selected === item.id;
    }
    if (mode === 'resource') {
      // For resource mode, handle both single and multi-select
      if (Array.isArray(selected)) {
        // Multi-select: check if item.id is in the selected array
        return selected.some((s: any) => s.id === item.id || s === item.id);
      }
      // Single select: compare by id
      return selected?.id === item.id || selected === item.id;
    }
    // Generic fallback: handle both array (multi-select) and single select
    if (Array.isArray(selected)) {
      return selected.some((s: any) => s === item.id || s?.id === item.id);
    }
    return selected?.id === item.id || selected === item.id;
  };

  const renderCreateSupplierForm = () => {
    if (mode !== 'supplier' || !config.allowCreateSupplier || !onSupplierCreated) {
      return null;
    }

    return (
      <CreateSupplierForm
        searchTerm={searchTerm}
        onSupplierCreated={onSupplierCreated}
      />
    );
  };

  return (
    <>
      {renderContextIndicator()}
      {renderSelectAllButtons()}
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {renderLoadingState()}
        {renderEmptyState()}
        {renderRemovalOption()}
        {renderNotAssignedOption()}
        {renderFrequentSuppliers()}
        {renderDataItems()}
      </div>
      {renderCreateSupplierForm()}
    </>
  );
};